/**
 * Reports API
 *
 * All reports support optional company_id filtering for per-client reporting.
 * When using the workspace context, reports automatically filter to the selected company.
 */
import apiClient from './index'
import type { ApiResponse, DateRangeParams } from '@/types'

/**
 * Extended params that include company filtering
 */
export interface ReportParams extends DateRangeParams {
  company_id?: string
}

export interface RevenueReport {
  period: {
    start_date: string
    end_date: string
  }
  company_id?: string | null
  total_revenue: number
  invoice_count: number
  average_invoice: number
  by_month: { month: string; revenue: number }[]
  top_companies: { company_id: string; name: string; revenue: number }[]
  top_projects: { project_id: string; name: string; revenue: number }[]
}

export interface UtilizationReport {
  period: {
    start_date: string
    end_date: string
    working_days: number
  }
  company_id?: string | null
  summary: {
    total_hours: number
    billable_hours: number
    target_hours: number
    team_utilization: number
    team_billable_rate: number
  }
  by_user: {
    user_id: string
    name: string
    email: string
    total_hours: number
    billable_hours: number
    non_billable_hours: number
    target_hours: number
    utilization_rate: number
    billable_rate: number
  }[]
}

export interface ProjectProfitabilityReport {
  project_id: string
  name: string
  status: string
  budget: number
  total_hours: number
  billable_hours: number
  labor_cost: number
  revenue: number
  profit: number
  margin: number
  budget_used: number
}

export interface SalesPipelineReport {
  period: {
    start_date: string
    end_date: string
  }
  company_id?: string | null
  summary: {
    total_deals: number
    won_deals: number
    lost_deals: number
    conversion_rate: number
    average_deal_value: number
    total_won_value: number
  }
  current_pipeline: {
    count: number
    value: number
    weighted_value: number
  }
  by_stage: { stage: string; count: number; value: number }[]
}

export interface TimeByProjectReport {
  period: {
    start_date: string
    end_date: string
  }
  company_id?: string | null
  summary: {
    total_hours: number
    billable_hours: number
    billable_rate: number
  }
  by_project: {
    project_id: string | null
    name: string
    total_hours: number
    billable_hours: number
    non_billable_hours: number
  }[]
}

export interface AgingReceivablesReport {
  company_id?: string | null
  summary: {
    total_outstanding: number
    total_overdue: number
    invoice_count: number
  }
  buckets: {
    current: { label: string; count: number; amount: number }
    '1_30': { label: string; count: number; amount: number }
    '31_60': { label: string; count: number; amount: number }
    '61_90': { label: string; count: number; amount: number }
    over_90: { label: string; count: number; amount: number }
  }
  details: Record<
    string,
    {
      count: number
      amount: number
      invoices: {
        invoice_id: string
        invoice_number: string
        company_name: string | null
        due_date: string
        balance: number
        days_overdue: number
      }[]
    }
  >
}

/**
 * Master client overview - shows all clients at once
 */
export interface ClientOverviewSummary {
  id: string
  name: string
  industry: string
  tier: string
  status: string
  // Quick stats
  contacts_count: number
  active_deals: number
  pipeline_value: number
  total_revenue: number
  active_projects: number
  recent_activities: number
  // Integration status
  integrations: {
    ga_enabled: boolean
    ads_enabled: boolean
    social_enabled: boolean
    platforms: string[]
  }
  // Feature flags
  features: Record<string, boolean>
  external_crm: string
  setup_notes: string
  // Branding
  logo_url: string
  primary_color: string
}

export interface ClientsOverviewReport {
  period: {
    name: string
    start_date: string
    end_date: string
  }
  totals: {
    total_clients: number
    total_contacts: number
    total_active_deals: number
    total_pipeline_value: number
    total_revenue: number
    total_active_projects: number
    clients_with_ga: number
    clients_with_ads: number
  }
  clients: ClientOverviewSummary[]
  generated_at: string
}

export const reportsApi = {
  /**
   * Get revenue report
   * @param params - Date range and optional company_id filter
   */
  async getRevenueReport(params?: ReportParams): Promise<RevenueReport> {
    const response = await apiClient.get<ApiResponse<RevenueReport>>('/reports/revenue', { params })
    return response.data.data
  },

  /**
   * Get utilization report
   * @param params - Date range and optional company_id filter
   */
  async getUtilizationReport(params?: ReportParams): Promise<UtilizationReport> {
    const response = await apiClient.get<ApiResponse<UtilizationReport>>('/reports/utilization', { params })
    return response.data.data
  },

  /**
   * Get project profitability report
   * @param status - Filter by project status (active, completed, all)
   * @param companyId - Optional company filter
   */
  async getProjectProfitabilityReport(status?: string, companyId?: string): Promise<{ company_id?: string; projects: ProjectProfitabilityReport[] }> {
    const response = await apiClient.get<ApiResponse<{ company_id?: string; projects: ProjectProfitabilityReport[] }>>('/reports/project-profitability', {
      params: { status, company_id: companyId },
    })
    return response.data.data
  },

  /**
   * Get sales pipeline report
   * @param params - Date range and optional company_id filter
   */
  async getSalesPipelineReport(params?: ReportParams): Promise<SalesPipelineReport> {
    const response = await apiClient.get<ApiResponse<SalesPipelineReport>>('/reports/sales-pipeline', { params })
    return response.data.data
  },

  /**
   * Get time by project report
   * @param params - Date range and optional company_id filter
   */
  async getTimeByProjectReport(params?: ReportParams): Promise<TimeByProjectReport> {
    const response = await apiClient.get<ApiResponse<TimeByProjectReport>>('/reports/time-by-project', { params })
    return response.data.data
  },

  /**
   * Get aging receivables report
   * @param companyId - Optional company filter
   */
  async getAgingReceivablesReport(companyId?: string): Promise<AgingReceivablesReport> {
    const response = await apiClient.get<ApiResponse<AgingReceivablesReport>>('/reports/aging-receivables', {
      params: companyId ? { company_id: companyId } : undefined,
    })
    return response.data.data
  },

  /**
   * Get master overview of all clients
   * Shows all clients at once with key metrics for high-level dashboard
   * @param params.period - Time period for activity metrics
   * @param params.sort - Sort by: name, revenue, deals, activity, pipeline
   * @param params.tier - Filter by company tier
   */
  async getClientsOverview(params?: {
    period?: string
    sort?: 'name' | 'revenue' | 'deals' | 'activity' | 'pipeline'
    tier?: string
  }): Promise<ClientsOverviewReport> {
    const response = await apiClient.get<ApiResponse<ClientsOverviewReport>>('/reports/clients/overview', { params })
    return response.data.data
  },

  // ===========================================================================
  // AI-Powered Report Analysis
  // ===========================================================================

  /**
   * Analyze report data with AI
   */
  async analyzeReport(data: AiAnalyzeReportRequest): Promise<AiReportAnalysis> {
    const response = await apiClient.post<ApiResponse<AiReportAnalysis>>('/reports/ai/analyze', data)
    return response.data.data
  },

  /**
   * Generate AI executive report for a company
   */
  async getAiExecutiveReport(
    companyId: string,
    options?: { period?: string; include_analytics?: boolean; include_ads?: boolean }
  ): Promise<AiExecutiveReportResponse> {
    const response = await apiClient.get<ApiResponse<AiExecutiveReportResponse>>(
      `/reports/ai/executive/${companyId}`,
      { params: options }
    )
    return response.data.data
  },

  /**
   * Convert natural language to report parameters
   */
  async queryReportNaturalLanguage(query: string): Promise<AiReportQueryResult> {
    const response = await apiClient.post<ApiResponse<AiReportQueryResult>>('/reports/ai/query', { query })
    return response.data.data
  },

  /**
   * Compare two periods with AI analysis
   */
  async comparePeriods(data: AiComparePeriodRequest): Promise<AiPeriodComparison> {
    const response = await apiClient.post<ApiResponse<AiPeriodComparison>>('/reports/ai/compare', data)
    return response.data.data
  },

  /**
   * Get unified client dashboard data
   * Aggregates CRM, Analytics, Ads, Social, and Projects into one response
   */
  async getUnifiedDashboard(companyId: string, period?: string): Promise<UnifiedDashboard> {
    const response = await apiClient.get<ApiResponse<UnifiedDashboard>>(
      `/reports/companies/${companyId}/unified-dashboard`,
      { params: { period: period || 'last_30_days' } }
    )
    return response.data.data
  },
}

// =============================================================================
// AI Report Types
// =============================================================================

export interface AiAnalyzeReportRequest {
  report_type: string
  report_data: Record<string, unknown>
  period?: string
  comparison_data?: Record<string, unknown>
}

export interface AiReportAnalysis {
  summary: string
  key_findings: string[]
  trends: Array<{
    metric: string
    direction: 'up' | 'down' | 'stable'
    change_description: string
    significance: 'high' | 'medium' | 'low'
  }>
  insights: Array<{
    insight: string
    impact: 'positive' | 'negative' | 'neutral'
    category: string
  }>
  recommendations: Array<{
    action: string
    reason: string
    priority: 'high' | 'medium' | 'low'
    expected_impact: string
  }>
  alerts: string[]
  health_score: number
  health_status: 'excellent' | 'good' | 'fair' | 'poor'
}

export interface AiExecutiveReportResponse {
  company_id: string
  company_name: string
  period: string
  report: AiExecutiveReport
  data_sources: {
    crm: boolean
    analytics: boolean
    ads: boolean
  }
}

export interface AiExecutiveReport {
  executive_summary: string
  highlights: string[]
  performance_narrative: string
  challenges: string[]
  opportunities: string[]
  recommendations: Array<{
    action: string
    rationale: string
    priority: 'high' | 'medium' | 'low'
  }>
  outlook: string
  key_metrics_commentary: Record<string, string>
}

export interface AiReportQueryResult {
  interpretation: string
  report_type: string
  suggested_reports: string[]
  filters: {
    date_range?: {
      start_date: string
      end_date: string
    }
    company_id?: string
    status?: string
  }
  metrics_to_highlight: string[]
  visualization_suggestions: string[]
  confidence: number
}

export interface AiComparePeriodRequest {
  current_period: Record<string, unknown>
  previous_period: Record<string, unknown>
  metric_names: string[]
  current_label?: string
  previous_label?: string
}

export interface AiPeriodComparison {
  summary: string
  overall_trend: 'improving' | 'declining' | 'stable' | 'mixed'
  metric_comparisons: Array<{
    metric: string
    current_value: number
    previous_value: number
    change_percent: number
    change_direction: 'up' | 'down' | 'stable'
    interpretation: string
    is_positive_change: boolean
  }>
  notable_changes: string[]
  concerns: string[]
  positive_developments: string[]
  recommendations: string[]
}

// =============================================================================
// Unified Client Dashboard Types
// =============================================================================

export interface UnifiedDashboard {
  company: {
    id: string
    name: string
    industry: string
    website: string
  }
  branding: {
    logo_url: string
    primary_color: string
    secondary_color: string
    company_name: string
  }
  period: {
    name: string
    start_date: string
    end_date: string
  }
  crm: {
    contacts_total: number
    contacts_new: number
    lifecycle_breakdown: Record<string, number>
    deals_total: number
    deals_new: number
    deals_active: number
    deals_won_period: number
    pipeline_value: number
    revenue_period: number
    revenue_total: number
    stage_breakdown: Record<string, { count: number; value: number }>
    activities_total: number
    activity_breakdown: Record<string, number>
  }
  leads: {
    total_contacts: number
    new_leads: number
    lifecycle_breakdown: Record<string, number>
    conversion_rate: number
  }
  projects: {
    total: number
    active: number
    total_budget: number
    total_hours_logged: number
    items: Array<{
      id: string
      name: string
      status: string
      project_type: string
      completion_percentage: number
      budget: number | null
      billing_type: string
      start_date: string | null
      end_date: string | null
      estimated_hours: number | null
      actual_hours: number
    }>
  }
  analytics: {
    overview: Record<string, number>
    traffic_sources?: Array<Record<string, unknown>>
    top_pages?: Array<Record<string, unknown>>
    campaigns?: Array<Record<string, unknown>>
    error?: string
  } | null
  ads: {
    platforms: string[]
    summary: {
      total_spend: number
      total_impressions: number
      total_clicks: number
      total_conversions: number
      avg_cpc: number
      avg_cpm: number
      ctr: number
    }
    by_platform: Record<string, { spend: number; impressions: number; clicks: number; conversions: number }>
    campaigns: Array<Record<string, unknown>>
    daily_trends: Array<Record<string, unknown>>
    error?: string
  } | null
  social: {
    accounts: Array<{ platform: string; name: string; handle: string }>
    analytics: Record<string, unknown>
    error?: string
  } | null
  changelog: Array<{
    id: string
    title: string
    description: string
    category: string
    platform: string | null
    impact: string | null
    change_date: string
    report_month: string
    include_in_report: boolean
    created_by_name: string
    created_at: string
  }>
  integrations: {
    ga_enabled: boolean
    ads_enabled: boolean
    ads_platforms: string[]
    social_enabled: boolean
  }
  generated_at: string
}
